import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, KeyRound, Power, PowerOff, Search, Eye, Loader2, User as UserIcon, Layers, ShieldCheck } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { fmtDateTime } from './radiusUtils';

const EMPTY = { username: '', name: '', password: '', group: '', enabled: true };

export default function RadiusUsers({ onChanged }) {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState('');
  const [delUser, setDelUser] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState('');
  const [nasGroups, setNasGroups] = useState([]);
  const [nasUser, setNasUser] = useState(null);
  const [nasMode, setNasMode] = useState('all');
  const [nasGroupIds, setNasGroupIds] = useState([]);
  const [nasSaving, setNasSaving] = useState(false);
  const [nasLoading, setNasLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, g, ng] = await Promise.all([
        api.get('/radius/users'),
        api.get('/radius/groups'),
        api.get('/radius/nas-groups').catch(() => ({ data: { items: [] } })),
      ]);
      setItems(u.data.items || []);
      setGroups(g.data.items || []);
      setNasGroups(ng.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged && onChanged(); };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, username: it.username, name: it.name || '', group: it.group || '', enabled: it.enabled }); setOpen(true); };

  const save = async () => {
    if (!editing && !form.username) return toast.error('Username wajib diisi');
    if (!editing && !form.password) return toast.error('Password wajib diisi');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/radius/users/${encodeURIComponent(editing.username)}`, { name: form.name, group: form.group || null, enabled: form.enabled });
        toast.success('User diperbarui');
      } else {
        await api.post('/radius/users', { username: form.username, password: form.password, name: form.name, group: form.group || null, enabled: form.enabled });
        toast.success('User RADIUS dibuat');
      }
      setOpen(false); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const changePw = async () => {
    if (!pw) return toast.error('Password baru wajib diisi');
    try { await api.post(`/radius/users/${encodeURIComponent(pwUser.username)}/password`, { password: pw }); toast.success('Password diubah'); setPwUser(null); setPw(''); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (it) => {
    setBusy(it.username);
    try {
      await api.post(`/radius/users/${encodeURIComponent(it.username)}/${it.enabled ? 'disable' : 'enable'}`);
      toast.success(it.enabled ? 'User dinonaktifkan' : 'User diaktifkan');
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(''); }
  };

  const doDelete = async () => {
    try { await api.delete(`/radius/users/${encodeURIComponent(delUser.username)}`); toast.success('User dihapus'); setDelUser(null); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const showDetail = async (it) => {
    try { const { data } = await api.get(`/radius/users/${encodeURIComponent(it.username)}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openNasAccess = async (it) => {
    setNasUser(it); setNasLoading(true); setNasMode('all'); setNasGroupIds([]);
    try {
      const { data } = await api.get(`/radius/users/${encodeURIComponent(it.username)}/nas-access`);
      setNasMode(data.mode || 'all');
      setNasGroupIds(data.group_ids || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setNasLoading(false); }
  };

  const toggleNasGroup = (id) => setNasGroupIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

  const saveNasAccess = async () => {
    if (nasMode === 'groups' && nasGroupIds.length === 0) return toast.error('Pilih minimal satu grup NAS, atau pilih "Allow all"');
    setNasSaving(true);
    try {
      const { data } = await api.put(`/radius/users/${encodeURIComponent(nasUser.username)}/nas-access`, { mode: nasMode, group_ids: nasMode === 'groups' ? nasGroupIds : [] });
      if (data?.warning) toast.warning(data.warning);
      toast.success('Akses NAS user diperbarui');
      setNasUser(null); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setNasSaving(false); }
  };

  const filtered = items.filter((it) => !q || it.username.toLowerCase().includes(q.toLowerCase()) || (it.name || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari username / nama…" className="pl-8" data-testid="radius-user-search" />
        </div>
        <Button onClick={openAdd} data-testid="radius-user-add"><Plus className="w-4 h-4 mr-1.5" /> Add User</Button>
      </div>
      <Card className="border-border"><CardContent className="p-3">
        <div className="border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-muted/40 hover:bg-muted/40">{['Username','Nama','Group','NAS Akses','Status','Last Login','Action'].map((h) => <TableHead key={h} className="text-xs whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">Memuat…</TableCell></TableRow>)}
              {!loading && filtered.length === 0 && (<TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-muted-foreground">Belum ada user RADIUS. Klik “Add User”.</TableCell></TableRow>)}
              {!loading && filtered.map((it) => (
                <TableRow key={it.username} data-testid={`radius-user-row-${it.username}`}>
                  <TableCell className="text-sm font-medium font-mono">{it.username}</TableCell>
                  <TableCell className="text-xs">{it.name || '—'}</TableCell>
                  <TableCell className="text-xs">{it.group ? <Badge variant="outline" className="text-[10px]">{it.group}</Badge> : '—'}</TableCell>
                  <TableCell className="text-xs">
                    {it.nas_access === 'groups'
                      ? <Badge className="text-[10px] bg-sky-500/15 text-sky-600 dark:text-sky-300 border border-sky-500/30" data-testid={`radius-user-nas-badge-${it.username}`}><Layers className="w-3 h-3 mr-1" />{(it.nas_group_ids || []).length} grup</Badge>
                      : <Badge variant="outline" className="text-[10px]" data-testid={`radius-user-nas-badge-${it.username}`}>Semua NAS</Badge>}
                  </TableCell>
                  <TableCell>{it.enabled ? <Badge className="text-[10px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30">Active</Badge> : <Badge className="text-[10px] bg-rose-500/15 text-rose-600 dark:text-rose-300 border border-rose-500/30">Disabled</Badge>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDateTime(it.last_login)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => showDetail(it)} data-testid={`radius-user-detail-${it.username}`}><Eye className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => openEdit(it)} data-testid={`radius-user-edit-${it.username}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => openNasAccess(it)} title="Atur akses NAS" data-testid={`radius-user-nas-${it.username}`}><ShieldCheck className="w-3.5 h-3.5 text-sky-500" /></Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setPwUser(it)} data-testid={`radius-user-pw-${it.username}`}><KeyRound className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-8" disabled={busy === it.username} onClick={() => toggle(it)} data-testid={`radius-user-toggle-${it.username}`}>{busy === it.username ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : it.enabled ? <PowerOff className="w-3.5 h-3.5 text-amber-500" /> : <Power className="w-3.5 h-3.5 text-emerald-500" />}</Button>
                      <Button size="sm" variant="ghost" className="h-8 text-rose-500" onClick={() => setDelUser(it)} data-testid={`radius-user-del-${it.username}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="radius-user-form">
          <SheetHeader><SheetTitle>{editing ? 'Edit User RADIUS' : 'Add User RADIUS'}</SheetTitle>
            <SheetDescription>Password disimpan sebagai Cleartext-Password di tabel radcheck untuk authentication FreeRADIUS. Password existing tidak pernah ditampilkan.</SheetDescription></SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs">Username</Label><div className="mt-1"><Input value={form.username} disabled={!!editing} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="nocadmin" data-testid="radius-user-f-username" /></div></div>
            <div><Label className="text-xs">Nama</Label><div className="mt-1"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="NOC Administrator" data-testid="radius-user-f-name" /></div></div>
            {!editing && (<div><Label className="text-xs">Password</Label><div className="mt-1"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="radius-user-f-password" /></div></div>)}
            <div><Label className="text-xs">Group / Role</Label><div className="mt-1">
              <Select value={form.group || 'none'} onValueChange={(v) => setForm({ ...form, group: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="radius-user-f-group"><SelectValue placeholder="Pilih group" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Tanpa group —</SelectItem>{groups.map((g) => <SelectItem key={g.groupname} value={g.groupname}>{g.groupname} ({g.mikrotik_group})</SelectItem>)}</SelectContent>
              </Select></div></div>
            <div className="flex items-center justify-between border border-border rounded-md px-3 py-2">
              <div><div className="text-sm font-medium">Enabled</div><div className="text-xs text-muted-foreground">Nonaktif = Auth-Type Reject (login ditolak)</div></div>
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="radius-user-f-enabled" />
            </div>
          </div>
          <SheetFooter className="mt-4"><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} disabled={saving} data-testid="radius-user-save">{saving ? 'Menyimpan…' : 'Simpan'}</Button></SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog open={!!pwUser} onOpenChange={(o) => { if (!o) { setPwUser(null); setPw(''); } }}>
        <DialogContent data-testid="radius-user-pw-dialog">
          <DialogHeader><DialogTitle>Ganti Password</DialogTitle><DialogDescription>User <span className="font-mono">{pwUser?.username}</span>. Password baru langsung berlaku untuk authentication RADIUS.</DialogDescription></DialogHeader>
          <div><Label className="text-xs">Password Baru</Label><div className="mt-1"><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="radius-user-pw-input" /></div></div>
          <DialogFooter><Button variant="outline" onClick={() => { setPwUser(null); setPw(''); }}>Batal</Button><Button onClick={changePw} data-testid="radius-user-pw-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md" data-testid="radius-user-detail">
          <SheetHeader><SheetTitle className="flex items-center gap-2"><UserIcon className="w-4 h-4" /> {detail?.username}</SheetTitle></SheetHeader>
          {detail && (<div className="mt-4 space-y-3 text-sm">
            {[['Nama', detail.name || '—'], ['Group', detail.group || '—'], ['Status', detail.enabled ? 'Active' : 'Disabled'], ['Last Login', fmtDateTime(detail.last_login)], ['Active Sessions', String(detail.active_sessions)], ['Dibuat', fmtDateTime(detail.created_at)]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-border pb-2"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>
            ))}
          </div>)}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus User RADIUS?</AlertDialogTitle><AlertDialogDescription>User <span className="font-mono">{delUser?.username}</span> akan dihapus dari radcheck & radusergroup. Riwayat log tetap tersimpan.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="radius-user-del-confirm">Ya, Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!nasUser} onOpenChange={(o) => { if (!o) setNasUser(null); }}>
        <DialogContent className="max-w-lg" data-testid="radius-user-nas-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sky-500" /> Akses NAS — {nasUser?.username}</DialogTitle>
            <DialogDescription>Tentukan pada NAS mana user ini boleh melakukan autentikasi login.</DialogDescription>
          </DialogHeader>
          {nasLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat…</div>
          ) : (
            <div className="space-y-3">
              <RadioGroup value={nasMode} onValueChange={setNasMode} className="space-y-2">
                <label className="flex items-start gap-3 border border-border rounded-md px-3 py-2.5 cursor-pointer hover:bg-muted/40" data-testid="radius-user-nas-mode-all">
                  <RadioGroupItem value="all" id="nas-mode-all" className="mt-0.5" />
                  <div><div className="text-sm font-medium">Allow all — semua NAS</div><div className="text-xs text-muted-foreground">User bisa login di seluruh NAS terdaftar (mis. akun manager).</div></div>
                </label>
                <label className="flex items-start gap-3 border border-border rounded-md px-3 py-2.5 cursor-pointer hover:bg-muted/40" data-testid="radius-user-nas-mode-groups">
                  <RadioGroupItem value="groups" id="nas-mode-groups" className="mt-0.5" />
                  <div><div className="text-sm font-medium">Hanya grup NAS tertentu</div><div className="text-xs text-muted-foreground">User hanya boleh autentikasi pada NAS di grup yang dipilih.</div></div>
                </label>
              </RadioGroup>

              {nasMode === 'groups' && (
                <div>
                  <Label className="text-xs flex items-center justify-between">
                    <span className="inline-flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Pilih Grup NAS</span>
                    <span className="text-muted-foreground">{nasGroupIds.length} dipilih</span>
                  </Label>
                  <div className="mt-1 border border-border rounded-md max-h-56 overflow-y-auto divide-y divide-border" data-testid="radius-user-nas-grouplist">
                    {nasGroups.length === 0 && <div className="text-xs text-muted-foreground px-3 py-4 text-center">Belum ada grup NAS. Buat dulu di tab “NAS Groups”.</div>}
                    {nasGroups.map((g) => (
                      <label key={g.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`radius-user-nas-group-${g.id}`}>
                        <Checkbox checked={nasGroupIds.includes(g.id)} onCheckedChange={() => toggleNasGroup(g.id)} />
                        <div className="min-w-0"><div className="text-sm font-medium truncate">{g.name}</div><div className="text-[11px] text-muted-foreground truncate">{g.member_count} NAS · {g.description || 'tanpa deskripsi'}</div></div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNasUser(null)}>Batal</Button>
            <Button onClick={saveNasAccess} disabled={nasSaving || nasLoading} data-testid="radius-user-nas-save">{nasSaving ? 'Menyimpan…' : 'Simpan Akses'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
